import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Alert,
  Skeleton,
  Card,
  CardContent,
  Stack,
  Chip,
  Button,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
} from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import InsertChartIcon from "@mui/icons-material/InsertChartOutlined";
import dayjs, { type Dayjs } from "dayjs";
import { getUsageHistory, getApiKeys } from "@/api/dashboard";
import type { RenderLogEntry, ApiKeyItem } from "@/api/dashboard";
import EmptyState from "@/components/EmptyState";
import ResponsiveTableContainer from "@/components/ResponsiveTableContainer";

// --- Helpers ---

function formatFileSize(bytes: number | null): string {
  if (bytes === null) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(ms: number | null): string {
  if (ms === null) return "-";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

const statusColors: Record<
  string,
  "success" | "warning" | "error" | "info" | "default"
> = {
  completed: "success",
  processing: "info",
  queued: "warning",
  failed: "error",
  canceled: "default",
  expired: "default",
};

const STATUS_OPTIONS = ["completed", "failed", "queued", "processing"] as const;

// --- Columns ---

const allColumns: GridColDef[] = [
  {
    field: "id",
    headerName: "Job ID",
    width: 280,
    renderCell: (params) => (
      <Typography variant="body2" fontFamily="monospace" fontSize={12}>
        {params.value}
      </Typography>
    ),
  },
  {
    field: "status",
    headerName: "Status",
    width: 120,
    renderCell: (params) => (
      <Chip
        label={params.value}
        color={statusColors[params.value as string] ?? "default"}
        size="small"
        variant="outlined"
      />
    ),
  },
  {
    field: "inputType",
    headerName: "Input",
    width: 80,
    renderCell: (params) => (
      <Chip label={params.value} size="small" variant="filled" />
    ),
  },
  {
    field: "pageCount",
    headerName: "Pages",
    width: 80,
    type: "number",
    renderCell: (params) => params.value ?? "-",
  },
  {
    field: "fileSize",
    headerName: "Size",
    width: 100,
    renderCell: (params) => formatFileSize(params.value as number | null),
  },
  {
    field: "renderTimeMs",
    headerName: "Render Time",
    width: 110,
    renderCell: (params) => formatDuration(params.value as number | null),
  },
  {
    field: "isTest",
    headerName: "Test",
    width: 70,
    renderCell: (params) =>
      params.value ? (
        <Chip label="Test" size="small" color="secondary" variant="outlined" />
      ) : null,
  },
  {
    field: "createdAt",
    headerName: "Created",
    width: 200,
    renderCell: (params) => new Date(params.value as string).toLocaleString(),
  },
  {
    field: "actions",
    headerName: "",
    width: 60,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      const token = params.row.downloadToken as string | null;
      if (!token) {
        return (
          <IconButton size="small" disabled aria-label="download unavailable">
            <DescriptionIcon fontSize="small" />
          </IconButton>
        );
      }
      return (
        <Tooltip title="View PDF">
          <IconButton
            size="small"
            component="a"
            href={`/dl/${token}/inline`}
            target="_blank"
            aria-label="view PDF"
          >
            <DescriptionIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      );
    },
  },
];

// --- CSV Export ---

function exportToCsv(rows: RenderLogEntry[]) {
  const headers = [
    "Job ID",
    "Status",
    "Input Type",
    "Pages",
    "Size (bytes)",
    "Render Time (ms)",
    "Total Time (ms)",
    "Test",
    "Created",
    "Completed",
  ];
  const csvRows = [
    headers.join(","),
    ...rows.map((r) =>
      [
        r.id,
        r.status,
        r.inputType,
        r.pageCount ?? "",
        r.fileSize ?? "",
        r.renderTimeMs ?? "",
        r.totalTimeMs ?? "",
        r.isTest ? "true" : "false",
        r.createdAt,
        r.completedAt ?? "",
      ].join(","),
    ),
  ];
  const blob = new Blob([csvRows.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `render-logs-${dayjs().format("YYYY-MM-DD")}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

// --- Empty-state overlays ---

function NoUsageOverlay() {
  return (
    <EmptyState
      icon={
        <InsertChartIcon aria-hidden="true" fontSize="large" color="disabled" />
      }
      title="No usage this period"
      description="Once you start making API calls, your usage will appear here."
    />
  );
}

function NoMatchesOverlay() {
  return (
    <EmptyState
      icon={
        <InsertChartIcon aria-hidden="true" fontSize="large" color="disabled" />
      }
      title="No matches"
      description="Try clearing or adjusting your filters."
    />
  );
}

// --- Component ---

export default function UsagePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const mobileColumnFields = new Set([
    "status",
    "inputType",
    "createdAt",
    "actions",
  ]);
  const columns = isMobile
    ? allColumns.filter((col) => mobileColumnFields.has(col.field))
    : allColumns;

  // Data state
  const [rows, setRows] = useState<RenderLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });

  // Filter state
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [apiKeyList, setApiKeyList] = useState<ApiKeyItem[]>([]);
  const [selectedApiKeyId, setSelectedApiKeyId] = useState<string>("");
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [jobIdInput, setJobIdInput] = useState("");
  const [jobIdSearch, setJobIdSearch] = useState("");

  // Detail panel
  const [selectedRow, setSelectedRow] = useState<RenderLogEntry | null>(null);

  // Debounce job ID search
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleJobIdChange = (value: string) => {
    setJobIdInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setJobIdSearch(value);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500);
  };

  // Load API keys
  useEffect(() => {
    getApiKeys()
      .then(setApiKeyList)
      .catch(() => {});
  }, []);

  // Toggle status filter
  const toggleStatus = (status: string) => {
    setStatusFilters((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      };
      if (statusFilters.length > 0) params.status = statusFilters.join(",");
      if (selectedApiKeyId) params.apiKeyId = selectedApiKeyId;
      if (startDate) params.startDate = startDate.format("YYYY-MM-DD");
      if (endDate) params.endDate = endDate.format("YYYY-MM-DD");
      if (jobIdSearch) params.jobId = jobIdSearch;

      const data = await getUsageHistory(params);
      setRows(data.renders);
      setTotal(data.total);
      setError(null);
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data
              ?.error
          : "Failed to load render logs";
      setError(msg ?? "Failed to load render logs");
    } finally {
      setLoading(false);
    }
  }, [
    paginationModel,
    statusFilters,
    selectedApiKeyId,
    startDate,
    endDate,
    jobIdSearch,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const hasFilters =
    statusFilters.length > 0 ||
    selectedApiKeyId !== "" ||
    startDate !== null ||
    endDate !== null ||
    jobIdSearch !== "";

  const handleClearFilters = () => {
    setStatusFilters([]);
    setSelectedApiKeyId("");
    setStartDate(null);
    setEndDate(null);
    setJobIdInput("");
    setJobIdSearch("");
    setPaginationModel({ page: 0, pageSize: 25 });
  };

  // Suppress unused dayjs warning
  void dayjs;

  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom>
        Usage &amp; History
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Stack spacing={2}>
            {/* Row 1: Status chips */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <FilterListIcon color="action" />
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Status:
              </Typography>
              {STATUS_OPTIONS.map((status) => (
                <Chip
                  key={status}
                  label={status}
                  size="small"
                  color={statusColors[status] ?? "default"}
                  variant={
                    statusFilters.includes(status) ? "filled" : "outlined"
                  }
                  onClick={() => toggleStatus(status)}
                  sx={{ cursor: "pointer" }}
                />
              ))}
            </Stack>

            {/* Row 2: Other filters */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              flexWrap="wrap"
            >
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="api-key-filter-label">API Key</InputLabel>
                <Select
                  labelId="api-key-filter-label"
                  value={selectedApiKeyId}
                  label="API Key"
                  onChange={(e) => {
                    setSelectedApiKeyId(e.target.value);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                >
                  <MenuItem value="">All keys</MenuItem>
                  {apiKeyList.map((k) => (
                    <MenuItem key={k.id} value={k.id}>
                      {k.name} ({k.type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(val) => {
                  setStartDate(val);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { width: { xs: "100%", sm: 170 } },
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(val) => {
                  setEndDate(val);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { width: { xs: "100%", sm: 170 } },
                  },
                }}
              />

              <TextField
                label="Job ID"
                size="small"
                value={jobIdInput}
                onChange={(e) => handleJobIdChange(e.target.value)}
                sx={{ width: { xs: "100%", sm: 240 } }}
                placeholder="Search by job ID..."
              />

              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => exportToCsv(rows)}
                disabled={rows.length === 0}
              >
                Export CSV
              </Button>

              {hasFilters && (
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                >
                  Clear filters
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Data table */}
      {loading && !rows.length ? (
        <Skeleton variant="rounded" height={500} />
      ) : (
        <ResponsiveTableContainer>
          <DataGrid
            rows={rows}
            columns={columns}
            rowCount={total}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            loading={loading}
            disableRowSelectionOnClick
            slots={{
              noRowsOverlay: hasFilters ? NoMatchesOverlay : NoUsageOverlay,
            }}
            onRowClick={(params) => {
              const row = params.row as RenderLogEntry;
              setSelectedRow((prev) => (prev?.id === row.id ? null : row));
            }}
            autoHeight
            sx={{
              bgcolor: "background.paper",
              borderRadius: 1,
              minWidth: isMobile ? 720 : 1100,
              "& .MuiDataGrid-row": {
                cursor: "pointer",
              },
            }}
          />
        </ResponsiveTableContainer>
      )}

      {/* Detail panel */}
      {selectedRow && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6" component="h2">
                  Job Details
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setSelectedRow(null)}
                  aria-label="close detail"
                >
                  <ClearIcon />
                </IconButton>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Job ID
                  </Typography>
                  <Typography
                    variant="body2"
                    fontFamily="monospace"
                    fontSize={12}
                  >
                    {selectedRow.id}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedRow.status}
                    color={statusColors[selectedRow.status] ?? "default"}
                    size="small"
                    variant="outlined"
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Input Type
                  </Typography>
                  <Chip
                    label={selectedRow.inputType}
                    size="small"
                    variant="filled"
                  />
                </Box>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Pages
                  </Typography>
                  <Typography variant="body2">
                    {selectedRow.pageCount ?? "-"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    File Size
                  </Typography>
                  <Typography variant="body2">
                    {formatFileSize(selectedRow.fileSize)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Render Time
                  </Typography>
                  <Typography variant="body2">
                    {formatDuration(selectedRow.renderTimeMs)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Pipeline Time
                  </Typography>
                  <Typography variant="body2">
                    {formatDuration(selectedRow.totalTimeMs)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Test Mode
                  </Typography>
                  <Typography variant="body2">
                    {selectedRow.isTest ? "Yes" : "No"}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {new Date(selectedRow.createdAt).toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                  <Typography variant="body2">
                    {selectedRow.completedAt
                      ? new Date(selectedRow.completedAt).toLocaleString()
                      : "-"}
                  </Typography>
                </Box>
              </Stack>

              {selectedRow.status === "failed" && (
                <Alert severity="error">
                  This render failed. Check your input and options, then try
                  again.
                </Alert>
              )}

              {selectedRow.downloadToken && (
                <Box>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<DescriptionIcon />}
                    component="a"
                    href={`/dl/${selectedRow.downloadToken}/inline`}
                    target="_blank"
                  >
                    View PDF
                  </Button>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
